import React, { useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import DateFormatter from 'components/DateFormatter';
import { currencyToLocal, INVOICE_STATUS } from 'utils/constants';
import './mobile.css';

const MOBILE_INVOICE_TABS = ['Overview', 'Audit'];

const getInvoiceStatus = (invoice) =>
  INVOICE_STATUS?.[invoice?.invoiceStatus] || invoice?.invoiceStatus || 'N/A';

const getStatusClass = (value) => {
  const normalizedValue = `${value || ''}`.toLowerCase();

  if (normalizedValue.includes('fully') || normalizedValue.includes('full')) {
    return 'success';
  }

  if (normalizedValue.includes('partial')) {
    return 'warning';
  }

  if (
    normalizedValue.includes('unpaid') ||
    normalizedValue.includes('pending')
  ) {
    return 'danger';
  }

  return 'muted';
};

const getInvoiceAmount = (invoice) =>
  currencyToLocal(invoice?.total, invoice?.rateUnit?.isoCurrencyCode);

const getInvoiceSections = (invoice) => ({
  Overview: [
    { label: 'Amount', value: getInvoiceAmount(invoice) },
    { label: 'Payment', value: getInvoiceStatus(invoice), tone: 'status' },
    { label: 'Date', value: <DateFormatter dateTime={invoice?.createdAt} /> },
  ],
  Audit: [
    {
      label: 'Created ID',
      value: invoice?.createdByUserId || 'N/A',
      type: 'createdUser',
    },
    {
      label: 'Updated ID',
      value: invoice?.updatedByUserId || 'N/A',
      type: 'updatedUser',
    },
  ],
});

const InvoiceMobileList = ({
  invoices,
  totalPages,
  currentPageNo,
  pageSize,
  handlePageChange,
  handleInvoiceClick,
  handleUserId,
}) => {
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [activeMobileTabs, setActiveMobileTabs] = useState({});

  const toggleMobileCard = (invoiceId) => {
    setExpandedCardId((prev) => (prev === invoiceId ? null : invoiceId));
  };

  const handleMobileTabChange = (invoiceId, tab) => {
    setActiveMobileTabs((prev) => ({
      ...prev,
      [invoiceId]: tab,
    }));
  };

  return (
    <>
      <div className="invoice-mobile-list">
        {(invoices || []).map((invoice) => {
          const activeTab =
            activeMobileTabs[invoice.id] || MOBILE_INVOICE_TABS[0];
          const sections = getInvoiceSections(invoice);
          const activeSection = sections[activeTab] || [];
          const status = getInvoiceStatus(invoice);
          const isExpanded = expandedCardId === invoice.id;

          return (
            <div className="invoice-mobile-card" key={invoice.id}>
              <div className="invoice-mobile-card__summary">
                <button
                  type="button"
                  className="invoice-mobile-card__identity"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleInvoiceClick(invoice);
                  }}
                >
                  <div className="invoice-mobile-card__name">
                    {invoice?.order?.buyer?.tradeName || 'N/A'}
                  </div>
                  <div className="invoice-mobile-card__meta">
                    {`Invoice ID ${invoice?.id || 'N/A'} | ${getInvoiceAmount(invoice)}`}
                  </div>
                </button>
                <div className="invoice-mobile-card__aside">
                  <span
                    className={`invoice-mobile-card__pill ${getStatusClass(status)}`}
                  >
                    {status}
                  </span>
                  <div className="invoice-mobile-card__tools">
                    <button
                      type="button"
                      className={`invoice-mobile-card__expand-toggle ${isExpanded ? 'is-open' : ''}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleMobileCard(invoice.id);
                      }}
                      aria-expanded={isExpanded}
                      aria-label={
                        isExpanded
                          ? 'Collapse invoice details'
                          : 'Expand invoice details'
                      }
                    >
                      <FaChevronDown />
                    </button>
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="invoice-mobile-card__body">
                  <div className="invoice-mobile-card__tabs">
                    {MOBILE_INVOICE_TABS.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        className={`invoice-mobile-card__tab ${activeTab === tab ? 'active' : ''}`}
                        onClick={() => handleMobileTabChange(invoice.id, tab)}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  <div className="invoice-mobile-card__detail-list">
                    {activeSection.map((item) => {
                      const isUserLink =
                        item.type === 'createdUser' ||
                        item.type === 'updatedUser';
                      const userId =
                        item.type === 'createdUser'
                          ? invoice?.createdByUserId
                          : invoice?.updatedByUserId;

                      return (
                        <div
                          className="invoice-mobile-card__detail-row"
                          key={item.label}
                        >
                          <div className="invoice-mobile-card__detail-label">
                            {item.label}
                          </div>
                          <div
                            className={`invoice-mobile-card__detail-value ${isUserLink && userId ? 'link' : ''}`}
                            onClick={() => {
                              if (isUserLink && userId) {
                                handleUserId(invoice, userId);
                              }
                            }}
                            aria-hidden
                          >
                            {item.tone === 'status' ? (
                              <span
                                className={`invoice-mobile-card__pill ${getStatusClass(item.value)}`}
                              >
                                {item.value}
                              </span>
                            ) : (
                              item.value
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {totalPages ? (
        <div className="invoice-mobile-pagination">
          <span className="invoice-mobile-pagination__count">
            {currentPageNo} of {totalPages}
          </span>
          <select
            className="page-select form-select table-select f-12 fw-normal invoice-mobile-pagination__select"
            value={pageSize}
            onChange={(e) => {
              const nextPageSize = Number(e.target.value);
              handlePageChange({
                pageNo: totalPages === currentPageNo ? 1 : currentPageNo,
                pageSize: nextPageSize,
              });
            }}
          >
            {[10, 20, 30, 40, 50].map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
          <div className="pagination invoice-mobile-pagination__actions">
            <button
              type="button"
              onClick={() => handlePageChange({ pageNo: 1, pageSize })}
              disabled={currentPageNo <= 1}
            >
              {'<<'}
            </button>
            <button
              type="button"
              onClick={() =>
                handlePageChange({
                  pageNo: currentPageNo - 1,
                  pageSize,
                })
              }
              disabled={currentPageNo === 1}
            >
              {'<'}
            </button>
            <button
              type="button"
              onClick={() =>
                handlePageChange({
                  pageNo: currentPageNo + 1,
                  pageSize,
                })
              }
              disabled={currentPageNo >= totalPages}
            >
              {'>'}
            </button>
            <button
              type="button"
              onClick={() =>
                handlePageChange({
                  pageNo: totalPages,
                  pageSize,
                })
              }
              disabled={currentPageNo >= totalPages}
            >
              {'>>'}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default InvoiceMobileList;
